import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { KeyboardEvent, SyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DropdownProps } from 'semantic-ui-react';
import { Button, Dropdown } from 'semantic-ui-react';
import { useOnClickOutside } from 'usehooks-ts';

import {
  KeyValueEditor,
  type KeyValueEditorProps,
  type KeyValueRecord,
} from './key-value-editor';
import s from './key-value-editor-dropdown.scss';

export interface KeyValueEditorDropdownProps
  extends
    Omit<
      KeyValueEditorProps,
      'autoAddFirstRow' | 'className' | 'onChange' | 'onValidChange' | 'value'
    >,
    Omit<DropdownProps, 'onChange' | 'value'> {
  /**
   * Additional CSS class applied to the dropdown root.
   */
  className?: string;
  /**
   * Empty state text shown in the dropdown trigger when no value is set.
   *
   * @default "Add values"
   */
  emptyText?: string;
  /**
   * Callback when the user applies changes.
   */
  onChange: (value: KeyValueRecord) => void;
  /**
   * Current value as a Record of key-value pairs.
   */
  value?: KeyValueRecord;
}

/**
 * Dropdown-based key-value pair editor. Wraps `KeyValueEditor` inside a
 * dropdown panel with Apply/Cancel buffering — changes are only committed when
 * the user clicks Apply.
 *
 * @see KeyValueEditor
 */
export const KeyValueEditorDropdown = ({
  className,
  disabled,
  emptyText = 'Add values',
  itemName,
  keyPlaceholder,
  maxLength,
  maxRows,
  onChange,
  value: externalValue,
  valuePlaceholder,
  valueRequired,
  ...dropdownProps
}: KeyValueEditorDropdownProps) => {
  useStyles(s);

  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  // Last applied value (used for Cancel → restore).
  const [appliedValue, setAppliedValue] = useState<KeyValueRecord | undefined>(
    externalValue,
  );

  // Draft value while the panel is open.
  const [draftValue, setDraftValue] = useState<KeyValueRecord | undefined>(
    externalValue,
  );

  const [isOpen, setIsOpen] = useState(false);

  // Sync with external value changes.
  useEffect(() => {
    setAppliedValue(externalValue);

    if (!isOpen) setDraftValue(externalValue);
  }, [externalValue, isOpen]);

  // Check if all draft rows are valid (for enabling Apply button).
  // Driven by the inline editor's onValidChange callback.
  const [allRowsValid, setAllRowsValid] = useState(true);

  const handleApply = useCallback(() => {
    const finalValue = draftValue ?? {};

    onChange(finalValue);
    setAppliedValue(finalValue);
    setIsOpen(false);
  }, [draftValue, onChange]);

  const handleCancel = useCallback(() => {
    setDraftValue(appliedValue);
    setIsOpen(false);
  }, [appliedValue]);

  useOnClickOutside(dropdownMenuRef, () => {
    if (!isOpen) return;
    handleCancel();
  });

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') handleCancel();
  };

  /**
   * Prevents key events inside the editor panel from bubbling to the Dropdown,
   * which would otherwise intercept keys like Space for accessibility purposes.
   * Only Escape is allowed to bubble for closing the dropdown.
   */
  const handlePanelKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key !== 'Escape') event.stopPropagation();
  }, []);

  const handleOpen = useCallback(() => {
    if (isOpen) return;

    setIsOpen(true);
    setDraftValue(appliedValue);
  }, [appliedValue, isOpen]);

  const displayText = useMemo(() => {
    if (!appliedValue || Object.keys(appliedValue).length === 0) {
      return emptyText;
    }

    const entries = Object.entries(appliedValue);
    const [firstKey, firstValue] = entries[0];
    const remaining = entries.length - 1;

    return (
      <span className={s.displayText}>
        <span className={s.displayKey}>{firstKey}</span>
        <span className={s.displaySeparator}>&nbsp;:&nbsp;</span>
        <span className={s.displayValue}>{firstValue}</span>

        {remaining > 0 && (
          <span className={s.displayMore}> + {remaining} more</span>
        )}
      </span>
    );
  }, [appliedValue, emptyText]);

  return (
    <Dropdown
      className={classNames(s.dropdown, className)}
      aria-label="Key Value Editor"
      open={isOpen}
      onOpen={handleOpen}
      onKeyDown={handleKeyDown}
      trigger={displayText}
      disabled={disabled}
      basic
      button
      floating
      {...dropdownProps}
    >
      <Dropdown.Menu
        ref={dropdownMenuRef}
        onClick={(event: SyntheticEvent) => event.stopPropagation()}
        onKeyDown={handlePanelKeyDown}
      >
        <div className={s.panel}>
          <KeyValueEditor
            autoFocus={isOpen}
            disabled={disabled}
            itemName={itemName}
            keyPlaceholder={keyPlaceholder}
            maxLength={maxLength}
            maxRows={maxRows}
            onChange={setDraftValue}
            onValidChange={setAllRowsValid}
            value={draftValue}
            valuePlaceholder={valuePlaceholder}
            valueRequired={valueRequired}
          />

          <div className={s.actions}>
            <Button content="Cancel" onClick={handleCancel} compact basic />

            <Button
              content="Apply"
              disabled={!allRowsValid}
              onClick={handleApply}
              primary
              compact
            />
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default KeyValueEditorDropdown;
